import { createId } from "./idUtils";
import { sanitizeColumnChildren } from "./columnContentUtils";
import { createDefaultNewsletterName } from "../utils/documentNameUtils";

export const DEFAULT_COLUMN_PROPS = {
  padding: "0",
  contentVAlign: "top",
  backgroundColor: "",
  border: {
    advanced: false,
    width: 0,
    style: "none",
    color: "#e2e8f0",
    radius: 0,
    top: { width: 0, style: "none", color: "#e2e8f0", radius: 0 },
    right: { width: 0, style: "none", color: "#e2e8f0", radius: 0 },
    bottom: { width: 0, style: "none", color: "#e2e8f0", radius: 0 },
    left: { width: 0, style: "none", color: "#e2e8f0", radius: 0 },
  },
};

export const DEFAULT_GRID_PROPS = {
  backgroundColor: "#ffffff",
  padding: "0",
  cellGap: 0,
  widthPercent: 100,
  contentAlign: "left",
  margins: { top: 0, right: 0, bottom: 0, left: 0 },
  border: { ...DEFAULT_COLUMN_PROPS.border },
};

export const createEmptyColumn = (widthPercent = 100) => ({
  id: createId("col"),
  widthPercent,
  props: { ...DEFAULT_COLUMN_PROPS, border: { ...DEFAULT_COLUMN_PROPS.border } },
  children: [],
});

export const rebalanceRowHeights = (rows) => {
  if (!rows?.length) {
    return;
  }
  const height = Math.floor(100 / rows.length);
  const remainder = 100 - height * rows.length;
  rows.forEach((row, index) => {
    row.heightPercent = height + (index === 0 ? remainder : 0);
  });
};

export const createEmptyRow = (columnCount = 1, heightPercent = 100) => {
  const width = Math.floor(100 / columnCount);
  const remainder = 100 - width * columnCount;
  const columns = Array.from({ length: columnCount }, (_, index) =>
    createEmptyColumn(width + (index === 0 ? remainder : 0))
  );
  return { id: createId("row"), heightPercent, columns };
};

export const createEmptyGrid = (rowCount = 1, columnCount = 1, name = "Layout") => {
  const rowHeight = Math.floor(100 / rowCount);
  const rowRemainder = 100 - rowHeight * rowCount;
  return {
    type: "grid",
    id: createId("grid"),
    name,
    props: { ...DEFAULT_GRID_PROPS, border: { ...DEFAULT_GRID_PROPS.border } },
    rows: Array.from({ length: rowCount }, (_, rowIndex) =>
      createEmptyRow(columnCount, rowHeight + (rowIndex === 0 ? rowRemainder : 0))
    ),
  };
};

export const createEmptyLayout = () => ({
  type: "layout",
  id: createId("layout"),
  grids: [],
});

export const createEmptyDocument = (name = createDefaultNewsletterName()) => ({
  name,
  subject: "",
  updatedAt: new Date().toISOString(),
  layout: createEmptyLayout(),
});

export const normalizeDocument = (document) => {
  if (!document) {
    return createEmptyDocument();
  }
  const withLayoutNames = (layout) => {
    const next = { ...layout, grids: [...(layout.grids || [])] };
    next.grids.forEach((grid, index) => assignLayoutNames(grid, `Layout ${index + 1}`));
    return next;
  };
  const baseMeta = {
    name: document.name || createDefaultNewsletterName(),
    subject: document.subject || "",
    updatedAt: document.updatedAt || new Date().toISOString(),
  };
  if (document.layout?.type === "layout") {
    return { ...baseMeta, layout: withLayoutNames(document.layout) };
  }
  if (document.root?.type === "grid") {
    const grid = assignLayoutNames({ ...document.root }, "Layout 1");
    return {
      ...baseMeta,
      layout: withLayoutNames({
        type: "layout",
        id: createId("layout"),
        grids: [grid],
      }),
    };
  }
  return createEmptyDocument(document.name);
};

const assignLayoutNames = (grid, fallbackName) => {
  if (!grid.name) {
    grid.name = fallbackName;
  }
  const needsRowHeights = (grid.rows || []).some(
    (row) => row.heightPercent === undefined || row.heightPercent === null
  );
  if (needsRowHeights) {
    rebalanceRowHeights(grid.rows);
  }
  for (const row of grid.rows || []) {
    for (const column of row.columns || []) {
      sanitizeColumnChildren(column);
      let nestedIndex = 0;
      for (const child of column.children || []) {
        if (child.type === "grid") {
          nestedIndex += 1;
          assignLayoutNames(child, `Layout interno ${nestedIndex}`);
        }
      }
    }
  }
  return grid;
};

const visitGrid = (grid, visitor) => {
  if (!grid || grid.type !== "grid") {
    return;
  }
  visitor(grid, "grid");
  for (const row of grid.rows || []) {
    visitor(row, "row", grid);
    for (const column of row.columns || []) {
      visitor(column, "column", row);
      for (const child of column.children || []) {
        if (child.type === "grid") {
          visitGrid(child, visitor);
        } else if (child.type === "component") {
          visitor(child, "component", column);
        }
      }
    }
  }
};

export const findNodeById = (layout, targetId) => {
  if (!layout) {
    return null;
  }
  if (layout.id === targetId) {
    return { node: layout, nodeType: "layout", parent: null };
  }
  for (const grid of layout.grids || []) {
    if (grid.id === targetId) {
      return { node: grid, nodeType: "grid", parent: layout };
    }
    let found = null;
    visitGrid(grid, (node, nodeType, parent) => {
      if (node.id === targetId) {
        found = { node, nodeType, parent };
      }
    });
    if (found) {
      return found;
    }
  }
  return null;
};

export const findGridByColumnId = (layout, columnId) => {
  if (!layout || !columnId) {
    return null;
  }
  const searchGrid = (grid) => {
    for (const row of grid.rows || []) {
      for (const column of row.columns || []) {
        if (column.id === columnId) {
          return grid;
        }
        for (const child of column.children || []) {
          if (child.type === "grid") {
            const nested = searchGrid(child);
            if (nested) {
              return nested;
            }
          }
        }
      }
    }
    return null;
  };
  for (const grid of layout.grids || []) {
    const match = searchGrid(grid);
    if (match) {
      return match;
    }
  }
  return null;
};

export const findRowById = (layout, rowId) => {
  let result = null;
  const searchGrid = (grid, parentGrid) => {
    for (const row of grid.rows || []) {
      if (row.id === rowId) {
        result = { row, grid, parentGrid };
        return;
      }
      for (const column of row.columns || []) {
        for (const child of column.children || []) {
          if (child.type === "grid") {
            searchGrid(child, grid);
          }
        }
      }
    }
  };
  for (const grid of layout.grids || []) {
    searchGrid(grid, null);
    if (result) {
      break;
    }
  }
  return result;
};
 